import { createSelector } from 'reselect';
import { sortBy } from 'lodash';

import { shopProductsSortOptions } from '../shared/constants/shopProductsSortOptions';
import { shopProductsCategories } from '../shared/constants/shopProductsCategories';

const getShop = state => state.shop;
export const getShopProducts = createSelector(
  getShop,
  ({ filters, sortBy: sorting, products, basket }) => ({
    data: sortBy(
      products.data.filter(product => {
        if (!product.active) return false;
        if (!filters) return true;

        // eslint-disable-next-line no-restricted-syntax
        for (const [property, value] of Object.entries(filters)) {
          if (property === 'basket') return value ? basket.data.includes(product.id) : true;
          if (property === 'categories' && value === shopProductsCategories.free) {
            const pricing = (product.pricing ? product.pricing : []).find(({ active }) => active);

            return !!pricing && pricing.price === 0;
          }
          if (property === 'categories' && value === shopProductsCategories.trial) {
            return product.isFreeTrial;
          }

          const productValue = product[property];
          const matchValue = Array.isArray(productValue)
            ? productValue.includes(value)
            : productValue === value;

          if (value && !matchValue) return false;
        }

        return true;
      }),
      [
        item => {
          if (sorting === shopProductsSortOptions.price) {
            const pricing = (item.pricing ? item.pricing : []).find(({ active }) => active);

            return pricing.price;
          }

          return item[sorting];
        },
      ],
    ),
    isPending: products.isPending,
  }),
);

export const getProductsFromBasket = createSelector(
  getShop,
  ({ products, history: { basket } }) => ({
    data: products.data.filter(({ id }) => basket.data.includes(id)),
    isPending: products.isPending,
  }),
);

export const getPurchasedProducts = createSelector(
  getShop,
  ({ products, history: { purchases } }) => ({
    data: products.data.filter(({ id }) => purchases.includes(id)),
    isPending: products.isPending,
  }),
);

export const getFavoriteProducts = createSelector(
  getShop,
  ({ products, history: { favorites } }) => ({
    data: products.data.filter(({ id }) => favorites.includes(id)),
    isPending: products.isPending,
  }),
);

export const getProductsProperties = createSelector(getShop, ({ properties }) => properties);

export const getSingleActiveProducts = createSelector(getShop, ({ products }) => ({
  data: products.data.filter(({ isPackage, active }) => active && !isPackage),
  isPending: products.isPending,
}));

export const getScopeProductSources = createSelector(getProductsProperties, ({ data }) =>
  data.sources.filter(({ thirdParty }) => !thirdParty),
);

export const getThirdPartyProductSources = createSelector(getProductsProperties, ({ data }) =>
  data.sources.filter(({ thirdParty }) => thirdParty),
);

export const getProductById = productId =>
  createSelector(getShop, ({ products: { data } }) => data.find(({ id }) => id === productId));

export const getProductsByIds = productIds =>
  createSelector(getShop, ({ products: { data } }) =>
    productIds ? data.filter(({ id }) => productIds.includes(id)) : [],
  );

export const getHomeSubscriptions = createSelector(getShop, ({ products }) => ({
  isPending: products.isPending,
  data: products.data
    .filter(({ purchased, validUntil, trialStarted }) => {
      if (validUntil && new Date(validUntil) < new Date()) {
        return false;
      }

      return purchased || trialStarted;
    })
    .map(({ id, name, status }) => ({
      id,
      primaryText: name,
      status,
    })),
}));

export const getRelatedProducts = ({ productLine }) =>
  createSelector(getShop, ({ products }) => ({
    isPending: products.isPending,
    data: products.data.filter(({ productLines }) => {
      return productLines.includes(productLine);
    }),
  }));
